import { createServerFn } from "@tanstack/react-start";
import {
  type StripeEnv,
  createStripeClient,
  getStripeErrorMessage,
} from "@/lib/stripe.server";

/**
 * Guest payment for a confirmed stay: rooms, boats and extras in one charge.
 *
 * Amounts are never taken from the browser. The guest proves who they are with
 * the email + confirmation code they already use on /my-stay, and every line is
 * rebuilt server-side from the stay's order row and any charges the lodge added
 * afterwards (fuel, ice). Standing rates resolve to the catalogue prices in the
 * payment account so the dashboard reports real product names; anything with a
 * one-off amount is billed inline.
 */

/** Booking order line id -> catalogue price lookup key. */
const RATE_LOOKUP_KEYS: Record<string, string> = {
  "booking-lodging": "lodge_room_night_rate",
  "booking-boat": "boat_rental_day_rate",
  "booking-skiff": "skiff_rental_day_rate",
  "booking-processing": "fish_processing_box_rate",
};

type OrderItem = {
  id?: string;
  name?: string;
  qty?: number;
  unit_price_cents?: number;
};

type ExtraCharge = { label?: string; amount_cents?: number; note?: string | null };

export type StayCheckoutResult = { clientSecret: string; orderId: string } | { error: string };

export const createStayCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { email: string; code: string; returnUrl: string; environment: StripeEnv }) => {
      if (!data.email?.includes("@")) throw new Error("Enter the email you booked with.");
      if (!data.code || data.code.trim().length < 6) throw new Error("Enter your confirmation code.");
      return data;
    },
  )
  .handler(async ({ data }): Promise<StayCheckoutResult> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      const { data: rows } = await supabaseAdmin.rpc("lookup_booking", {
        _email: data.email.trim(),
        _code: data.code.trim(),
      });
      const stay = (rows ?? [])[0];
      if (!stay) return { error: "We couldn't match that email and confirmation code." };
      if ((stay.status ?? "").toLowerCase() !== "approved") {
        return { error: "Your dates aren't confirmed yet, so there's nothing to pay." };
      }

      const { data: order } = await supabaseAdmin
        .from("shop_orders")
        .select("id, status, items, total_cents")
        .eq("booking_request_id", stay.id)
        .maybeSingle();

      if (!order) return { error: "No balance is on file for this stay yet." };
      if ((order.status ?? "").toLowerCase() === "paid") {
        return { error: "This stay is already paid — thank you." };
      }

      const stripe = createStripeClient(data.environment);
      const items = (Array.isArray(order.items) ? order.items : []) as OrderItem[];
      const charges = (Array.isArray(stay.extra_charges)
        ? stay.extra_charges
        : []) as ExtraCharge[];

      // Resolve the catalogue prices once, keyed by lookup key.
      const wanted = [
        ...new Set(
          items
            .map((item) => (item.id ? RATE_LOOKUP_KEYS[item.id] : undefined))
            .filter((key): key is string => Boolean(key)),
        ),
      ];
      const catalogue = new Map<string, { id: string; unit_amount: number | null }>();
      if (wanted.length) {
        const prices = await stripe.prices.list({ lookup_keys: wanted, limit: 100 });
        for (const price of prices.data) {
          if (price.lookup_key) {
            catalogue.set(price.lookup_key, { id: price.id, unit_amount: price.unit_amount });
          }
        }
      }

      const lineItems: Record<string, unknown>[] = [];

      for (const item of items) {
        const qty = Math.max(1, Math.round(Number(item.qty ?? 1)));
        const unit = Math.round(Number(item.unit_price_cents ?? 0));
        if (!Number.isFinite(unit) || unit < 1) continue;

        const key = item.id ? RATE_LOOKUP_KEYS[item.id] : undefined;
        const price = key ? catalogue.get(key) : undefined;

        // Only use the catalogue price when it still matches what the guest
        // was quoted; otherwise charge exactly what's on their stay.
        if (price && price.unit_amount === unit) {
          lineItems.push({ price: price.id, quantity: qty });
        } else {
          lineItems.push({
            price_data: {
              currency: "usd",
              product_data: { name: item.name ?? "Stay charge" },
              unit_amount: unit,
            },
            quantity: qty,
          });
        }
      }

      for (const charge of charges) {
        const amount = Math.round(Number(charge.amount_cents ?? 0));
        if (!Number.isFinite(amount) || amount < 1) continue;
        lineItems.push({
          price_data: {
            currency: "usd",
            product_data: {
              name: charge.label ?? "Additional charge",
              ...(charge.note ? { description: charge.note } : {}),
            },
            unit_amount: amount,
          },
          quantity: 1,
        });
      }

      if (!lineItems.length) return { error: "There's nothing outstanding on this stay." };

      const session = await stripe.checkout.sessions.create({
        line_items: lineItems as never,
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer_email: data.email.trim(),
        payment_intent_data: {
          description: `Coffman Cove Lodging stay ${stay.confirmation_code}`,
        },
        metadata: { orderId: order.id, bookingId: stay.id },
      });

      return { clientSecret: session.client_secret ?? "", orderId: order.id };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
